import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import {
  Bell, Newspaper, CloudRain, Car, Briefcase, Tag, TrendingUp,
  Home, Shield, HeartPulse, Trophy, Plus, Filter, MapPin, Search,
  Globe, Settings, X, Loader2, Building2, BellRing, BellOff,
  ThumbsUp, ThumbsDown, CheckCircle, Eye, Upload, Award, MessageCircle, Map
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import usePushNotifications from "@/hooks/usePushNotifications";
import AIPalChat from "@/components/AIPalChat";
import { FALLBACK_ALERT_CATEGORIES, FALLBACK_ALERTS } from "@/data/dummyData";

// Lazy load map component for performance
const LocationMapPicker = lazy(() => import("@/components/LocationMapPicker"));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  breaking_news: Newspaper,
  weather: CloudRain,
  traffic: Car,
  jobs_funding: Briefcase,
  deals: Tag,
  stocks: TrendingUp,
  property: Home,
  security: Shield,
  health: HeartPulse,
  sports: Trophy
};

const CATEGORY_COLORS = {
  breaking_news: "bg-red-500",
  weather: "bg-blue-500",
  traffic: "bg-amber-500",
  jobs_funding: "bg-purple-500",
  deals: "bg-green-500",
  stocks: "bg-indigo-500",
  property: "bg-orange-500",
  security: "bg-slate-700",
  health: "bg-pink-500",
  sports: "bg-emerald-500"
};

// All countries list
const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria", "Bangladesh",
  "Belgium", "Brazil", "Canada", "Chile", "China", "Colombia", "Czech Republic", "Denmark",
  "Egypt", "Finland", "France", "Germany", "Ghana", "Greece", "Hong Kong", "India", "Indonesia",
  "Iran", "Iraq", "Ireland", "Israel", "Italy", "Japan", "Kenya", "Malaysia", "Mexico",
  "Morocco", "Netherlands", "New Zealand", "Nigeria", "Norway", "Pakistan", "Peru", "Philippines",
  "Poland", "Portugal", "Romania", "Russia", "Saudi Arabia", "Singapore", "South Africa",
  "South Korea", "Spain", "Sweden", "Switzerland", "Taiwan", "Thailand", "Turkey", "UAE",
  "Uganda", "Ukraine", "United Kingdom", "United States", "Venezuela", "Vietnam", "Zimbabwe"
];

export default function AlertsHub() {
  const [categories, setCategories] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [myFilters, setMyFilters] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateAlert, setShowCreateAlert] = useState(false);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [showAIPal, setShowAIPal] = useState(false);
  const [corporateStatus, setCorporateStatus] = useState(null);
  const [noticeBoard, setNoticeBoard] = useState([]);
  const [userCountry, setUserCountry] = useState("US");
  const [locationSearch, setLocationSearch] = useState("");
  const [locationResults, setLocationResults] = useState([]);
  const [searchingLocation, setSearchingLocation] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const { 
    isSupported: pushSupported, 
    isSubscribed: pushSubscribed, 
    permission: pushPermission,
    loading: pushLoading,
    subscribe: subscribePush,
    unsubscribe: unsubscribePush,
    sendTest: sendTestPush
  } = usePushNotifications();

  // Filter form state
  const [filterForm, setFilterForm] = useState({
    category: "",
    enabled: true,
    location_type: "none",
    countries: [],  // Changed to array for multi-select
    radius_km: 50,
    center_lat: null,
    center_lng: null
  });

  // Create alert form state
  const [alertForm, setAlertForm] = useState({
    category: "",
    title: "",
    content: "",
    location: null,
    media_url: null,
    media_type: null
  });

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/alerts/categories`, { headers: getHeaders() });
      setCategories(res.data.categories || []);
    } catch (err) {
      setCategories(FALLBACK_ALERT_CATEGORIES);
    }
  };

  const fetchAlerts = async (category = null) => {
    try {
      let url = `${API}/alerts/feed`;
      if (category) url += `?category=${category}`;
      const res = await axios.get(url, { headers: getHeaders() });
      const data = res.data.alerts || [];
      setAlerts(data.length > 0 ? data : FALLBACK_ALERTS);
    } catch (err) {
      setAlerts(FALLBACK_ALERTS);
    }
  };

  const fetchMyFilters = async () => {
    try {
      const res = await axios.get(`${API}/alerts/my-filters`, { headers: getHeaders() });
      setMyFilters(res.data.filters || []);
    } catch (err) {
      console.error("Failed to fetch filters", err);
    }
  };

  const fetchCorporateStatus = async () => {
    try {
      const res = await axios.get(`${API}/alerts/corporate/status`, { headers: getHeaders() });
      setCorporateStatus(res.data);
    } catch (err) {
      setCorporateStatus({ is_corporate: false });
    }
  };

  const fetchNoticeBoard = async () => {
    try {
      const res = await axios.get(`${API}/alerts/notice-board?limit=5`, { headers: getHeaders() });
      setNoticeBoard(res.data.rewards || []);
    } catch (err) {
      console.error("Failed to fetch notice board", err);
    }
  };

  const fetchUserCountry = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: getHeaders() });
      if (res.data.country) setUserCountry(res.data.country);
    } catch (err) {}
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchCategories(), fetchAlerts(), fetchMyFilters(), 
        fetchCorporateStatus(), fetchNoticeBoard(), fetchUserCountry()
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Search location using OpenStreetMap Nominatim
  const searchLocation = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setLocationResults([]);
      return;
    }
    
    setSearchingLocation(true);
    try {
      const res = await axios.get(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`,
        { headers: { "Accept-Language": "en" } }
      );
      setLocationResults(res.data.map(r => ({
        display_name: r.display_name,
        lat: parseFloat(r.lat),
        lng: parseFloat(r.lon),
        country: r.display_name.split(", ").pop()
      })));
    } catch (err) {
      console.error("Location search failed", err);
    }
    setSearchingLocation(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (locationSearch) searchLocation(locationSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [locationSearch, searchLocation]);

  const handleSaveFilter = async () => {
    if (!filterForm.category) {
      toast.error("Please select a category");
      return;
    }

    // Validate country selection (max 5)
    if (filterForm.location_type === "country" && filterForm.countries.length > 5) {
      toast.error("Maximum 5 countries allowed");
      return;
    }

    try {
      await axios.post(`${API}/alerts/filters`, {
        ...filterForm,
        country: filterForm.countries.join(",")  // Store as comma-separated
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" }
      });
      toast.success("Filter saved");
      setShowFilterDialog(false);
      fetchMyFilters();
      fetchAlerts(selectedCategory);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save filter");
    }
  };

  const handleDeleteFilter = async (category) => {
    try {
      await axios.delete(`${API}/alerts/filters/${category}`, { headers: getHeaders() });
      toast.success("Filter removed");
      fetchMyFilters();
    } catch (err) {
      toast.error("Failed to remove filter");
    }
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 2MB");
      return;
    }

    setUploadingMedia(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(`${API}/alerts/upload-media`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" }
      });

      setAlertForm({
        ...alertForm,
        media_url: res.data.url,
        media_type: res.data.content_type.startsWith("video") ? "video" : "image"
      });
      toast.success("Media uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploadingMedia(false);
  };

  const handleCreateAlert = async () => {
    if (!alertForm.title || !alertForm.content || !alertForm.category) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.post(`${API}/alerts/create`, alertForm, {
        headers: { ...getHeaders(), "Content-Type": "application/json" }
      });
      toast.success("Alert created!");
      setShowCreateAlert(false);
      setAlertForm({ category: "", title: "", content: "", location: null, media_url: null, media_type: null });
      fetchAlerts(selectedCategory);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create alert");
    }
  };

  const handleEngagement = async (alertId, action) => {
    try {
      await axios.post(`${API}/alerts/${alertId}/${action}`, {}, {
        headers: { ...getHeaders(), "Content-Type": "application/json" }
      });
      fetchAlerts(selectedCategory);
      if (action === "confirm") {
        toast.success("Alert confirmed! Thank you for verifying.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const handleSubscribeCorporate = async () => {
    try {
      await axios.post(`${API}/alerts/corporate/subscribe`, {}, {
        headers: { ...getHeaders(), "Content-Type": "application/json" }
      });
      toast.success("Corporate subscription activated!");
      fetchCorporateStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Subscription failed");
    }
  };

  const selectLocation = (loc) => {
    setFilterForm({
      ...filterForm,
      center_lat: loc.lat,
      center_lng: loc.lng
    });
    setAlertForm({
      ...alertForm,
      location: { lat: loc.lat, lng: loc.lng, name: loc.display_name, country: loc.country }
    });
    setLocationResults([]);
    setLocationSearch(loc.display_name);
  };

  const toggleCountrySelection = (country) => {
    const current = filterForm.countries;
    if (current.includes(country)) {
      setFilterForm({ ...filterForm, countries: current.filter(c => c !== country) });
    } else if (current.length < 5) {
      setFilterForm({ ...filterForm, countries: [...current, country] });
    } else {
      toast.error("Maximum 5 countries allowed");
    }
  };

  const getFilterForCategory = (catId) => {
    return myFilters.find(f => f.category === catId);
  };

  const Icon = ({ category }) => {
    const IconComponent = CATEGORY_ICONS[category] || Bell;
    return <IconComponent className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold flex items-center gap-3">
              <Bell className="w-8 h-8" />
              ALERTS HUB
            </h1>
            <p className="text-blue-100 mt-1">Stay informed with personalized alerts</p>
          </div>
          <div className="flex items-center gap-3">
            {/* AlertsPal Button */}
            <Button
              onClick={() => setShowAIPal(!showAIPal)}
              variant="outline"
              className="border-white hover:bg-white/20"
              data-testid="alertspal-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              AlertsPal
            </Button>
            
            {/* Push Notification Toggle */}
            {pushSupported && (
              <Button
                onClick={async () => {
                  if (pushSubscribed) {
                    const success = await unsubscribePush();
                    if (success) toast.success("Push notifications disabled");
                  } else {
                    const success = await subscribePush();
                    if (success) {
                      toast.success("Push notifications enabled!");
                      try { await sendTestPush(); } catch {}
                    } else if (pushPermission === 'denied') {
                      toast.error("Notifications blocked in browser settings");
                    }
                  }
                }}
                variant="outline"
                className={`border-white hover:bg-white/20 ${pushSubscribed ? 'bg-green-500/20' : ''}`}
                disabled={pushLoading}
                data-testid="push-toggle-btn"
              >
                {pushLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 
                  pushSubscribed ? <><BellRing className="w-4 h-4 mr-2" />On</> : 
                  <><BellOff className="w-4 h-4 mr-2" />Enable</>}
              </Button>
            )}
            {corporateStatus?.is_corporate ? (
              <Badge className="bg-amber-500 text-white">
                <Building2 className="w-4 h-4 mr-1" />Corporate
              </Badge>
            ) : (
              <Button onClick={handleSubscribeCorporate} variant="outline" className="border-white hover:bg-white/20" data-testid="subscribe-corporate-btn">
                <Building2 className="w-4 h-4 mr-2" />Corporate (100/mo)
              </Button>
            )}
            <Button onClick={() => setShowCreateAlert(true)} className="bg-white text-blue-600 hover:bg-blue-50" data-testid="create-alert-btn">
              <Plus className="w-4 h-4 mr-2" />Create Alert
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Notice Board */}
        {noticeBoard.length > 0 && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                <Award className="w-5 h-5" />
                Reward Notice Board
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {noticeBoard.map((reward, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-amber-800">
                    <Trophy className="w-4 h-4 text-amber-500" />
                    <span>
                      Congratulations to <strong>{reward.user?.name}</strong> for receiving{" "}
                       <strong>{reward.amount} {reward.amount === 1 ? "AI" : "AIs"}</strong> for alerting users about "{reward.alert_title}"
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar - Categories */}
          <div className="col-span-3">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Categories
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  <button
                    onClick={() => { setSelectedCategory(null); fetchAlerts(); }}
                    className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-app transition ${!selectedCategory ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                    data-testid="category-all"
                  >
                    <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-slate-800">All Alerts</p>
                    </div>
                  </button>
                  {categories.map(cat => {
                    const filter = getFilterForCategory(cat.id);
                    return (
                      <button
                        key={cat.id}
                        onClick={() => { setSelectedCategory(cat.id); fetchAlerts(cat.id); }}
                        className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-app transition ${selectedCategory === cat.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                        data-testid={`category-${cat.id}`}
                      >
                        <div className={`w-10 h-10 rounded-lg ${CATEGORY_COLORS[cat.id]} flex items-center justify-center text-white`}>
                          <Icon category={cat.id} />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-medium text-slate-800">{cat.name}</p>
                          {cat.requires_corporate && <Badge variant="outline" className="text-xs mt-1">Corp</Badge>}
                        </div>
                        {filter?.enabled && <div className="w-2 h-2 rounded-full bg-green-500" title="Filter active" />}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* My Filters */}
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    My Filters
                  </CardTitle>
                  <Button size="sm" variant="ghost" onClick={() => {
                    setFilterForm({ category: "", enabled: true, location_type: "none", countries: [], radius_km: 50, center_lat: null, center_lng: null });
                    setShowFilterDialog(true);
                  }} data-testid="add-filter-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {myFilters.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No filters set</p>
                ) : (
                  <div className="space-y-2">
                    {myFilters.map(filter => (
                      <div key={filter.category} className="flex items-center justify-between p-2 bg-app rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded ${CATEGORY_COLORS[filter.category]} flex items-center justify-center text-white`}>
                            <Icon category={filter.category} />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{categories.find(c => c.id === filter.category)?.name}</p>
                            {filter.location_type === "radius" && <p className="text-xs text-slate-500">{filter.radius_km}km radius</p>}
                            {filter.location_type === "country" && filter.country && (
                              <p className="text-xs text-slate-500">{filter.country.split(",").length} countries</p>
                            )}
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteFilter(filter.category)} className="text-red-500 hover:text-red-700">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Main Content - Alerts Feed */}
          <div className="col-span-9">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">
                    {selectedCategory ? categories.find(c => c.id === selectedCategory)?.name : "All Alerts"}
                  </CardTitle>
                  <Badge variant="outline">{alerts.length} alerts</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[65vh]">
                  {alerts.length === 0 ? (
                    <div className="text-center py-12">
                      <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No alerts found</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {alerts.map(alert => (
                        <Card key={alert.id} className="hover:shadow-md transition" data-testid={`alert-${alert.id}`}>
                          <CardContent className="p-4">
                            <div className="flex gap-4">
                              <div className={`w-12 h-12 rounded-lg ${CATEGORY_COLORS[alert.category]} flex items-center justify-center text-white shrink-0`}>
                                <Icon category={alert.category} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <h3 className="font-semibold text-slate-800">{alert.title}</h3>
                                    <p className="text-sm text-slate-600 mt-1">{alert.content}</p>
                                  </div>
                                  <Badge className={CATEGORY_COLORS[alert.category]}>
                                    {categories.find(c => c.id === alert.category)?.name}
                                  </Badge>
                                </div>
                                
                                {/* Media Preview */}
                                {alert.media_url && (
                                  <div className="mt-3">
                                    {alert.media_type === "video" ? (
                                      <video src={`${process.env.REACT_APP_BACKEND_URL}${alert.media_url}`} controls className="max-h-48 rounded-lg" />
                                    ) : (
                                      <img src={`${process.env.REACT_APP_BACKEND_URL}${alert.media_url}`} alt="" className="max-h-48 rounded-lg object-cover" />
                                    )}
                                  </div>
                                )}

                                {/* Alert Stats & Engagement */}
                                <div className="flex items-center gap-4 mt-4 pt-3 border-t">
                                  <div className="flex items-center gap-1 text-slate-500 text-sm">
                                    <Eye className="w-4 h-4" />
                                    <span>{alert.view_count || 0}</span>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEngagement(alert.id, "like")}
                                    className={`gap-1 ${alert.user_liked ? 'text-blue-600' : 'text-slate-500'}`}
                                    data-testid={`like-${alert.id}`}
                                  >
                                    <ThumbsUp className="w-4 h-4" />
                                    <span>{alert.likes || 0}</span>
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEngagement(alert.id, "unlike")}
                                    className={`gap-1 ${alert.user_unliked ? 'text-red-600' : 'text-slate-500'}`}
                                    data-testid={`unlike-${alert.id}`}
                                  >
                                    <ThumbsDown className="w-4 h-4" />
                                    <span>{alert.unlikes || 0}</span>
                                  </Button>
                                  {(alert.category === "security" || alert.category === "traffic") && (
                                    <Button
                                      size="sm"
                                      variant={alert.user_confirmed ? "default" : "outline"}
                                      onClick={() => handleEngagement(alert.id, "confirm")}
                                      className={`gap-1 ${alert.user_confirmed ? 'bg-green-500 hover:bg-green-600' : ''}`}
                                      disabled={alert.user_confirmed}
                                      data-testid={`confirm-${alert.id}`}
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                      <span>Confirm ({alert.confirmations || 0})</span>
                                    </Button>
                                  )}
                                </div>

                                <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                  {alert.location && (
                                    <span className="flex items-center gap-1">
                                      <MapPin className="w-3 h-3" />
                                      {alert.location.name || alert.location.country}
                                    </span>
                                  )}
                                  <span>{formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}</span>
                                  {alert.creator && <span>by {alert.creator.name}</span>}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* AlertsPal AI Chat */}
      {showAIPal && (
        <div className="fixed bottom-4 right-4 w-96 z-50">
          <AIPalChat
            palType="alertspal"
            palName="AlertsPal"
            onClose={() => setShowAIPal(false)}
          />
        </div>
      )}

      {/* Create Alert Dialog */}
      <Dialog open={showCreateAlert} onOpenChange={setShowCreateAlert}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Alert</DialogTitle>
            <DialogDescription>Share important information with the community</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Category *</Label>
              <Select value={alertForm.category} onValueChange={(v) => setAlertForm({ ...alertForm, category: v })}>
                <SelectTrigger data-testid="alert-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id} disabled={cat.requires_corporate && !corporateStatus?.is_corporate}>
                      <div className="flex items-center gap-2">
                        <Icon category={cat.id} />
                        {cat.name}
                        {cat.requires_corporate && <Badge variant="outline" className="ml-2 text-xs">10 AI</Badge>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Title *</Label>
              <Input value={alertForm.title} onChange={(e) => setAlertForm({ ...alertForm, title: e.target.value })} placeholder="Alert title" data-testid="alert-title-input" />
            </div>

            <div>
              <Label>Content *</Label>
              <Textarea value={alertForm.content} onChange={(e) => setAlertForm({ ...alertForm, content: e.target.value })} placeholder="Alert details..." rows={4} data-testid="alert-content-input" />
            </div>

            {/* Media Upload */}
            <div>
              <Label>Media (Optional - Max 2MB)</Label>
              <div className="mt-2">
                {alertForm.media_url ? (
                  <div className="relative inline-block">
                    {alertForm.media_type === "video" ? (
                      <video src={`${process.env.REACT_APP_BACKEND_URL}${alertForm.media_url}`} className="h-24 rounded" />
                    ) : (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${alertForm.media_url}`} alt="" className="h-24 rounded object-cover" />
                    )}
                    <Button size="sm" variant="destructive" className="absolute -top-2 -right-2 w-6 h-6 p-0 rounded-full" onClick={() => setAlertForm({ ...alertForm, media_url: null, media_type: null })}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-blue-400 transition">
                    {uploadingMedia ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5 text-slate-400" />}
                    <span className="text-sm text-slate-500">{uploadingMedia ? "Uploading..." : "Upload image or video"}</span>
                    <input type="file" accept="image/*,video/*" className="hidden" onChange={handleMediaUpload} disabled={uploadingMedia} />
                  </label>
                )}
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-3">
                <Map className="w-4 h-4" />
                Location (Optional)
              </Label>
              <Suspense fallback={<div className="h-48 bg-slate-100 rounded-lg flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>}>
                <LocationMapPicker
                  initialLocation={alertForm.location ? { lat: alertForm.location.lat, lng: alertForm.location.lng } : null}
                  showRadius={false}
                  onLocationChange={(loc) => {
                    setAlertForm({
                      ...alertForm,
                      location: { lat: loc.lat, lng: loc.lng, name: loc.name, country: loc.country }
                    });
                  }}
                  height="200px"
                />
              </Suspense>
              {alertForm.location && (
                <Badge className="mt-2" variant="secondary">
                  <MapPin className="w-3 h-3 mr-1" />
                  {alertForm.location.name?.substring(0, 50) || `${alertForm.location.lat.toFixed(4)}, ${alertForm.location.lng.toFixed(4)}`}
                </Badge>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreateAlert(false)}>Cancel</Button>
              <Button onClick={handleCreateAlert} data-testid="submit-alert-btn">Create Alert</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog open={showFilterDialog} onOpenChange={setShowFilterDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Set Alert Filter</DialogTitle>
            <DialogDescription>Customize how you receive alerts</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Category *</Label>
              <Select value={filterForm.category} onValueChange={(v) => setFilterForm({ ...filterForm, category: v })}>
                <SelectTrigger data-testid="filter-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2"><Icon category={cat.id} />{cat.name}</div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label>Enable Filter</Label>
              <Switch checked={filterForm.enabled} onCheckedChange={(v) => setFilterForm({ ...filterForm, enabled: v })} />
            </div>

            <div>
              <Label>Location Filter</Label>
              <Select value={filterForm.location_type} onValueChange={(v) => setFilterForm({ ...filterForm, location_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No location filter</SelectItem>
                  <SelectItem value="country">By countries (select up to 5)</SelectItem>
                  <SelectItem value="radius">By radius from my location</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {filterForm.location_type === "country" && (
              <div>
                <Label>Select Countries (max 5)</Label>
                <p className="text-xs text-slate-500 mb-2">Selected: {filterForm.countries.length}/5</p>
                <ScrollArea className="h-48 border rounded-lg p-2">
                  <div className="space-y-1">
                    <button
                      onClick={() => setFilterForm({ ...filterForm, countries: [] })}
                      className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-slate-100 ${filterForm.countries.length === 0 ? 'bg-blue-100 text-blue-700' : ''}`}
                    >
                      All Countries
                    </button>
                    {COUNTRIES.map(country => (
                      <button
                        key={country}
                        onClick={() => toggleCountrySelection(country)}
                        className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-slate-100 ${filterForm.countries.includes(country) ? 'bg-blue-100 text-blue-700' : ''}`}
                      >
                        {filterForm.countries.includes(country) && <CheckCircle className="w-4 h-4 inline mr-2" />}
                        {country}
                      </button>
                    ))}
                  </div>
                </ScrollArea>
                {filterForm.countries.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {filterForm.countries.map(c => (
                      <Badge key={c} variant="secondary" className="cursor-pointer" onClick={() => toggleCountrySelection(c)}>
                        {c} <X className="w-3 h-3 ml-1" />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            {filterForm.location_type === "radius" && (
              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <Map className="w-4 h-4" />
                  Select Location & Coverage Radius
                </Label>
                <Suspense fallback={<div className="h-64 bg-slate-100 rounded-lg flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>}>
                  <LocationMapPicker
                    initialLocation={filterForm.center_lat ? { lat: filterForm.center_lat, lng: filterForm.center_lng } : null}
                    initialRadius={filterForm.radius_km}
                    showRadius={true}
                    onLocationChange={(loc) => {
                      setFilterForm({
                        ...filterForm,
                        center_lat: loc.lat,
                        center_lng: loc.lng
                      });
                    }}
                    onRadiusChange={(r) => setFilterForm({ ...filterForm, radius_km: r })}
                    height="300px"
                    minRadius={5}
                    maxRadius={500}
                  />
                </Suspense>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowFilterDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveFilter} data-testid="save-filter-btn">Save Filter</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
